import type React from 'react';
import type {
  PaletteColor,
  PaletteColorOptions,
  SxProps,
  Theme,
  ThemeOptions,
} from '@mui/material';
import { alpha, darken, lighten } from '@mui/system';

import muiOverrides from './muiOverrides';
import type { Colors } from './paletteColors';
import themeColors from './paletteColors';
import typography from './typography';

export type Styles = Record<string, SxProps<Theme>>;

declare module '@mui/material/styles' {
  interface Theme {
    colors: Colors;
    alpha: typeof alpha;
    darken: typeof darken;
    lighten: typeof lighten;
    dimension: {
      headerHeight: {
        xs: number;
        sm: number;
        md: number;
        lg: number;
        xl: number;
      };
    };
  }

  interface ThemeOptions {
    colors: Colors;
    alpha?: typeof alpha;
    darken?: typeof darken;
    lighten?: typeof lighten;
    dimension?: {
      headerHeight?: {
        xs?: number;
        sm?: number;
        md?: number;
        lg?: number;
        xl?: number;
      };
    };
  }

  interface Palette {
    tertiary: PaletteColor;
  }

  interface PaletteOptions {
    tertiary: PaletteColorOptions;
  }

  interface TypographyVariants {
    fontWeightSemiBold: React.CSSProperties['fontWeight'];
    body3: React.CSSProperties;
    body4: React.CSSProperties;
    button2: React.CSSProperties;
    caption2: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    body3?: React.CSSProperties;
    body4?: React.CSSProperties;
    button2?: React.CSSProperties;
    caption2?: React.CSSProperties;
  }

  interface TypeBackground {
    grey: string;
    selected: string;
  }
}

declare module '@mui/material/SvgIcon' {
  interface SvgIconPropsColorOverrides {
    tertiary: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    tertiary: true;
  }

  interface ButtonPropsVariantOverrides {
    cta: true;
    ctaAlt: true;
    ctaBlue: true;
    ctaSecondary: true;
    ctaTertiary: true;
    squareCta: true;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body3: true;
    body4: true;
    button2: true;
    caption2: true;
  }
}

const baseTheme = {
  typography,
  ...muiOverrides,
};

// The theme is created in the LocalizedThemeProvider,
// because all its colors can be overwritten by a customization from the backend.
export const defaultTheme = (colors: Colors): ThemeOptions => {
  const _themeColors = themeColors(colors);

  return {
    alpha,
    darken,
    lighten,
    dimension: {
      headerHeight: {
        xs: 55,
        sm: 55,
        md: 55,
        lg: 180,
        xl: 180,
      },
    },

    palette: {
      background: _themeColors.background,
      primary: _themeColors.primary,
      secondary: _themeColors.secondary,
      success: _themeColors.success,
      error: _themeColors.error,
      tertiary: _themeColors.tertiary,
    },
    colors,
  };
};

export default baseTheme;
